import React, { useState, useEffect } from 'react';
import { useNavigate, useMatch } from 'react-router-dom';
import moment from 'moment';
import {
  Form, Spin, Card,
  Input, Row, Col,
  Button, DatePicker, Typography,
  TimePicker, Select, notification,
} from 'antd';
import {
  get, post, put, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { backendResponseCodes } from '../../../utils/responseMessages';
import './ManageNotification.scss';

const ManageNotification = (): React.ReactElement => {
  interface CategoryFromListAll {
    active?: boolean;
    id?: number;
    name?: string;
  }

  interface CreateNotification {
    categoria: number;
    titulo: string;
    mensagem: string;
    dataNotificacaoForm: moment.Moment;
    horaNotificacao: moment.Moment;
    dataExpiracaoForm: moment.Moment;
  }

  const navigate = useNavigate();
  const [formInstance] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(true);
  const [listCategories, setListCategories] = useState<Array<CategoryFromListAll>>([]);
  const routeMatchEdit = useMatch('/system/notifications/:id/edit');
  const [mode] = useState(!routeMatchEdit ? 'CREATE' : 'EDIT');

  /**
 * @name fetchNotificationData
 * @param {int} id
 * @description busca a notificação através do seu id para
 * carregar suas informações dentro da modal
 * @returns retorna a modal com as informações da notificação para edição
 */
  const fetchNotificationData = async (id: number): Promise<void> => {
    let findResponse: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: `/notification/findById/${id}`,

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      findResponse = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (findResponse?.body?.code === 7008):
        // "All notification listed successfully", 7008
        formInstance.setFieldsValue({
          categoria: findResponse
            ?.body
            .listAllNotificationOutput
            .notificationCategory
            .id,

          titulo: findResponse
            ?.body
            .listAllNotificationOutput
            .title,

          mensagem: findResponse
            ?.body
            .listAllNotificationOutput
            .body,

          dataNotificacaoForm: moment(
            findResponse
              ?.body
              .listAllNotificationOutput
              .desiredDateNotification).utc(),

          horaNotificacao: moment(findResponse
            ?.body
            .listAllNotificationOutput
            .desiredDateNotification),

          dataExpiracaoForm: moment(
            findResponse
              ?.body
              .listAllNotificationOutput
              .expirationDate).utc(),
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.Forbidden.description.pt_br,
        });
        break;

      case (responseError?.body?.code === -7001):
        // "Notification not found", -7001
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.Notification
            .NotificationNotFound.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Ocorreu um erro ao buscar a listagem de notificações. Tente novamente mais tarde.',
        });
        break;
    }
  };

  /**
   * @name handleSubmitNotification
   * @param {Form.useForm} formValues
   * @description gerencia a validação do formulário e envio de novo registro ou atualização
   * @returns retorna a criação ou atualização de um registro de notificação
   */
  const handleSubmitNotification = async (formValues: CreateNotification): Promise<void> => {
    let eventResponse: ResponseWrapper | undefined;
    let eventResponseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    if (moment(formValues.dataNotificacaoForm).format('YYYY-MM-DD[T]') > moment(formValues.dataExpiracaoForm).format('YYYY-MM-DD[T]')) {
      notification.warning({
        message: 'Aviso',
        description: 'A data de expiração não pode ser menor do que a data de notificação.',
      });
      return;
    }

    if (mode === 'EDIT' && routeMatchEdit?.params?.id == null) {
      notification.error({
        message: 'Error',
        description: 'Desculpe, houve um erro ao realizar esta ação.',
      });

      navigate('/system/notifications');
      return;
    }

    try {
      setLoading(true);

      const params: {
        id?: number,
        active?: boolean,
        notificationCategoryId: number,
        title: string,
        body: string,
        desiredDateNotification: string,
        expirationDate: string,
      } = {
        notificationCategoryId: formValues.categoria,
        title: formValues.titulo,
        body: formValues.mensagem,
        desiredDateNotification:
          moment(
            `${formValues.dataNotificacaoForm.format('YYYY-MM-DD[T]')}${formValues.horaNotificacao.format('HH:mm:ss.SSS[Z]')}`,
          ).utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        expirationDate: moment(formValues.dataExpiracaoForm).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      };

      if (mode === 'EDIT') {
        params.id = Number(routeMatchEdit?.params?.id);
        params.active = true;

        const requestReference: RequestWrapper = put({
          url: '/notification/editNotification',
          params,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        eventResponse = await requestReference;
      } else {
        const requestReference: RequestWrapper = post({
          url: '/notification/notificationRequest',
          params,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        eventResponse = await requestReference;
      }
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        eventResponseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (eventResponse?.body?.code === 7006):
        // Notification registered successfully
        notification.success({
          message: 'Sucesso',
          description: backendResponseCodes.Success.Notification
            .NotificationRegisteredSuccessfully.description.pt_br,
        });
        navigate('/system/notifications');
        break;

      case (eventResponse?.body?.code === 7007):
        // Notification edited successfully
        notification.success({
          message: 'Sucesso',
          description: 'Notificação atualizada.',
        });
        navigate('/system/notifications');
        break;

      case (eventResponseError?.body?.code === -20000):
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.NotificationCategory
            .NotificationCategoryNotFound.description.pt_br,
        });
        break;

      case (eventResponseError?.body?.code === -4001):
        break;

      case (eventResponseError?.body?.code === -4003):
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.Forbidden
            .description.pt_br,
        });
        break;

      case (eventResponseError?.body?.code === -4000):
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.SomethingUnexpectedHappened
            .description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao finalizar a requisição. Por favor, tente novamente mais tarde.',
        });
        break;
    }

    setLoading(false);
  };

  /**
   * @name getDataCategories
   * @description carregar a lista de categorias disponiveis para ser utilizadas nas notificações
   * @returns retorna uma lista populada em dropdown para cadastro de notificação
   */
  const getDataCategories = async (): Promise<void> => {
    let categoriesResponse: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = get({
      url: '/notificationCategory/listAll',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      categoriesResponse = await requestReference;
    } catch (error: unknown) {
      console.log('error load categories', error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (categoriesResponse?.body?.code === 18002):
        // "Notification category listed successfully", 18002
        setListCategories(categoriesResponse?.body.listAllNotificationCategoryOutput);
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.Forbidden.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro ao carregar as categorias. Por favor, tente novamente mais tarde.',
        });
        break;
    }
  };

  useEffect(() => {
    setLoading(false);
    getDataCategories(); // carrega lista de categorias para o dropdown

    const executeOnMount = async (): Promise<void> => {
      if (
        mode === 'EDIT'
        && routeMatchEdit?.params?.id != null
      ) {
        await fetchNotificationData(Number(routeMatchEdit.params.id));
      }
    };

    executeOnMount();

    return () => {
      setLoading(false);
    };
  }, []);

  return (
    <div className="ManageNotification">
      <Row
        className="ManageNotification__ComponentRow"
        gutter={[24, 24]}
      >
        <Col
          className="ManageNotification__ComponentColumn"
          xs={{ span: 24 }}
        >
          <Row
            gutter={[24, 24]}
            justify="space-between"
            className="ManageNotification__PageTitleRow"
          >
            <Col
              className="ManageNotification__PageTitleColumn"
            >
              <Typography.Title
                className="ManageNotification__PageTitle"
              >
                {
                  mode === 'CREATE'
                    ? 'Cadastrar notificação'
                    : 'Editar notificação'
                }
              </Typography.Title>
            </Col>

            <Col
              className="ManageNotification__ButtonColumn"
              xs={{ span: 24 }}
              sm={{ span: 24 }}
              md={{ span: 6 }}
              lg={{ span: 4 }}
              xl={{ span: 4 }}
            >
              <Row
                gutter={[24, 24]}
                justify="end"
                className="ManageNotification__NestedButtonRow"
              >
                <Col
                  xs={{ span: 24 }}
                  className="ManageNotification__BackButtonColumn"
                >
                  <Button
                    block
                    type="default"
                    href="/system/notifications"
                    className="ManageNotification__BackButton"
                    onClick={
                      (eventt): void => {
                        eventt.preventDefault();
                        navigate('/system/notifications');
                      }
                    }
                  >
                    Voltar
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <Row
            gutter={[24, 24]}
            className="ManageNotification__ContentRow"
          >
            <Col
              className="ManageNotification__ContentColumn"
              xs={{ span: 24 }}
            >
              <Card
                className="ManageNotification__Card"
              >
                <Spin
                  tip="Carregando..."
                  spinning={loading}
                >
                  <Form
                    form={formInstance}
                    scrollToFirstError
                    onFinish={
                      async (formValues): Promise<void> => {
                        handleSubmitNotification(formValues);
                      }
                    }
                    autoComplete="off"
                    layout="vertical"
                  >
                    <Row gutter={[24, 24]}>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 24 }}
                      >
                        <Form.Item
                          label={(
                            <Typography.Text
                              className="ManageNotification__DescriptionLabel ManageNotification__AllFormLabels"
                            >
                              Categoria
                            </Typography.Text>
                          )}
                          name="categoria"
                          rules={[
                            { required: true, message: 'Por favor, selecione ao menos uma categoria.' },
                          ]}
                        >
                          <Select
                            showSearch
                            optionFilterProp="children"
                            placeholder="Escolha uma categoria"
                          >
                            {
                              listCategories.map((v) => (
                                <Select.Option key={v.id} value={v.id}>
                                  {v.name}
                                </Select.Option>
                              ))
                            }
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 24]}>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 24 }}
                      >
                        <Form.Item
                          className="ManageNotification__ForceBottom"
                          label={(
                            <Typography.Text
                              className="ManageNotification__DescriptionLabel ManageNotification__AllFormLabels"
                            >
                              Título
                            </Typography.Text>
                          )}
                          name="titulo"
                          rules={[{
                            required: true,
                            message: 'Por favor, digite um título.',
                          }]}
                        >
                          <Input
                            placeholder="Digite um título"
                            maxLength={255}
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 24 }}
                      >
                        <Form.Item
                          className="ManageNotification__DescriptionFormItem"
                          name="mensagem"
                          label={(
                            <Typography.Text
                              className="ManageNotification__DescriptionLabel ManageNotification__AllFormLabels"
                            >
                              Mensagem
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              max: 500,
                              message: 'Descrição deve possuir no máximo 500 caracteres',
                            },
                            {
                              required: true,
                              message: 'Por favor, digite uma mensagem.',
                            },
                          ]}
                        >
                          <Input.TextArea
                            className="ManageNotification__DescriptionInput"
                            placeholder="Digite uma mensagem"
                            maxLength={500}
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 24]}>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 12 }}
                      >
                        <Form.Item
                          className="ManageNotification__DateFormItem"
                          name="dataNotificacaoForm"
                          label={(
                            <Typography.Text
                              className="ManageNotification__DateLabel ManageNotification__AllFormLabels"
                            >
                              Data da notificação
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Data é obrigatória',
                            },
                          ]}
                        >
                          <DatePicker
                            className="ManageNotification__DateInput"
                            format="DD/MM/YYYY"
                          />
                        </Form.Item>
                      </Col>

                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 12 }}
                      >
                        <Form.Item
                          className="ManageNotification__StartTimeFormItem"
                          name="horaNotificacao"
                          label={(
                            <Typography.Text
                              className="ManageNotification__StartTimeLabel ManageNotification__AllFormLabels"
                            >
                              Hora da notificação
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Hora é obrigatório',
                            },
                          ]}
                        >
                          <TimePicker
                            minuteStep={60}
                            className="ManageNotification__StartTimeInput"
                            format="HH:mm"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Row gutter={[24, 24]}>
                      <Col
                        xs={{ span: 24 }}
                        lg={{ span: 12 }}
                      >
                        <Form.Item
                          className="ManageNotification__DateFormItem"
                          name="dataExpiracaoForm"
                          label={(
                            <Typography.Text
                              className="ManageNotification__DateLabel ManageNotification__AllFormLabels"
                            >
                              Notificação ativa até qual data
                            </Typography.Text>
                          )}
                          rules={[
                            {
                              required: true,
                              message: 'Data é obrigatória',
                            },
                          ]}
                        >
                          <DatePicker
                            className="ManageNotification__DateInput"
                            format="DD/MM/YYYY"
                          />
                        </Form.Item>
                      </Col>
                    </Row>

                    <Form.Item
                      className="ManageNotification-form__item"
                      style={{
                        textAlign: 'right',
                        marginTop: '1rem',
                        marginBottom: '0',
                      }}
                    >
                      <Button
                        type="primary"
                        htmlType="submit"
                        className="btn-login"
                        loading={loading}
                      >
                        Salvar
                      </Button>
                    </Form.Item>
                  </Form>
                </Spin>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

ManageNotification.propTypes = {};
ManageNotification.defaultProps = {};

export default ManageNotification;
